import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Box, Paper, Divider, Select, MenuItem, FormControl,
    InputLabel, TextField, Button, Stack, Grid, IconButton, InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import { useAppStore } from '../../../../../store/appStore.tsx';
import { useValue } from '../../../../../context/ContextProvider';
import { updateConfigAlarma } from '../../../../../actions/alarmaActions.js';

// --- Opciones para los campos Select ---
const QOS_OPTIONS = [
    { value: 0, label: 'At most once (QoS 0)' },
    { value: 1, label: 'At least once (QoS 1)' },
    { value: 2, label: 'Exactly once (QoS 2)' },
];

// --- Generador del estado inicial para la configuración MQTT ---
// Se exporta para que el padre la use
export const generarEstadoInicialMqtt = () => ({
    broker: 'alarmascerberus.com',
    port: 1883,
    user: 'admin',
    pass: '123456',
    topicDev: 'ce/',
    topicServer: 'ce/server',
    topicBcast: 'ce/bcast',
    qos: 0,
    kalive: 30,
});

function ConfiguracionMqttForm({ config, setConfig, state, setState, originalConfigRef }) {
    const { jsonOutput, formDirty, jsonNeedsUpdate } = state;
    const [showPassword, setShowPassword] = useState(false);
    const theme = useTheme();
    const { currentUser, device, startLoading, endLoading } = useAppStore();
    const { dispatch } = useValue();

    useEffect(() => {
        // Genera el JSON inicial si está vacío
        if (!jsonOutput) {
            setState(prev => ({
                ...prev,
                jsonOutput: JSON.stringify(config, null, 2),
                jsonNeedsUpdate: false
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const originalConfigString = JSON.stringify(originalConfigRef.current);
        const currentConfigString = JSON.stringify(config);
        const isDirty = currentConfigString !== originalConfigString;
        
        let needsUpdate = true;
        try {
            const jsonObject = JSON.parse(jsonOutput || '{}');
            needsUpdate = JSON.stringify(jsonObject) !== currentConfigString;
        } catch (e) {
            needsUpdate = true;
        }

        if (isDirty !== formDirty || needsUpdate !== jsonNeedsUpdate) {
            setState(prev => ({
                ...prev,
                formDirty: isDirty,
                jsonNeedsUpdate: needsUpdate
            }));
        }

    }, [config, jsonOutput, formDirty, jsonNeedsUpdate, originalConfigRef, setState]);
    
    // --- MANEJADORES ---
    const handleChange = (event) => {
        const { name, value, type } = event.target;
        const valorFinal = type === 'number' ? parseInt(value, 10) || 0 : value;
        setConfig(prev => ({ ...prev, [name]: valorFinal }));
    };

    const handleGenerateJson = () => {
        setState(prev => ({
            ...prev,
            jsonOutput: JSON.stringify(config, null, 2),
            jsonNeedsUpdate: false
        }));
    };

    const sendConfig = async() => {
        /*//console.log("--- Enviando Configuración GPRS JSON ---", config);
        alert("Configuración GPRS enviada. Revisa la consola.");
        originalConfigRef.current = JSON.parse(JSON.stringify(config));
        setState(prev => ({ ...prev, formDirty: false }));
        */
        //console.log("--- Enviando Configuración JSON ---", config);
        startLoading();
        setState(prev => ({ ...prev, configId: "---", estado: "Esperando respuesta del servidor/dispositivo", color: "black"}));
        const updateResult = await updateConfigAlarma(currentUser, device.devId, "CMD_SET_CONFIG_MQTT", config, dispatch);
        //console.log("updateResult: ", updateResult);

        if (updateResult?.deviceResponse === 0) {
            // Si el envío es exitoso, actualizamos la configuración original
            originalConfigRef.current = JSON.parse(JSON.stringify(config));
            setState(prev => ({ ...prev, formDirty: false, configId: updateResult.insertId, estado: "Última configuración actualizada con éxito.", color: "green"}));
            alert(`Configuración enviada con éxito (ID: ${updateResult.insertId})`);
        } else {
            setState(prev => ({ ...prev, configId: 0, estado: "Error. Última configuración NO actualizada.", color: "red"}));
            alert("Error al enviar la configuración.");
        }
        endLoading();
    };


    const handleCancel = () => {
        if (window.confirm("¿Seguro que quieres descartar los cambios?")) {
            setConfig(originalConfigRef.current);
        }
    };

    const getLabelFromValue = (options, value) => options.find(o => o.value === value)?.label || 'N/A';

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" component="h1" gutterBottom>Configuración MQTT</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box component="form" onSubmit={(e) => { e.preventDefault(); sendConfig(); }}>
                    <Grid container spacing={4} sx={{ alignItems: 'stretch' }}>
                        {/* Columna izquierda: Formulario */}
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}><TextField fullWidth name="broker" label="Broker" value={config.broker} onChange={handleChange} /></Grid>
                                    <Grid item xs={12}><TextField fullWidth type="number" name="port" label="Puerto" value={config.port} onChange={handleChange} /></Grid>
                                    <Grid item xs={12}><TextField fullWidth name="user" label="Usuario" value={config.user} onChange={handleChange} /></Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            name="pass"
                                            label="Contraseña"
                                            type={showPassword ? 'text' : 'password'}
                                            value={config.pass}
                                            onChange={handleChange}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}><TextField fullWidth name="topicDev" label="Raiz Tópico Dispositivo" value={config.topicDev} onChange={handleChange} /></Grid>
                                    <Grid item xs={12}><TextField fullWidth name="topicServer" label="Tópico Servidor" value={config.topicServer} onChange={handleChange} /></Grid>
                                    <Grid item xs={12}><TextField fullWidth name="topicBcast" label="Tópico Broadcast" value={config.topicBcast} onChange={handleChange} /></Grid>
                                    <Grid item xs={12}>
                                        <TextField select fullWidth name="qos" label="QoS" value={config.qos} onChange={handleChange}>
                                            {QOS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12}><TextField fullWidth type="number" name="kalive" label="KeepAlive (seg)" value={config.kalive} onChange={handleChange} InputProps={{ inputProps: { min: 10, max: 600 } }} /></Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Columna derecha: JSON y Resumen */}
                        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Paper variant="outlined" sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>
                                    JSON DE CONFIGURACIÓN
                                </Typography>
                                <TextField value={jsonOutput} multiline readOnly fullWidth sx={{ flexGrow: 1, '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' }, '& .MuiInputBase-input': { fontFamily: 'monospace', height: '100% !important', overflowY: 'auto !important', backgroundColor: jsonNeedsUpdate ? '#ffe0b2' : 'white' }}}/>
                                <Button fullWidth variant="contained" color={jsonNeedsUpdate ? "warning" : "primary"} onClick={handleGenerateJson} sx={{ mt: 1 }}>
                                    {jsonNeedsUpdate ? "Actualizar JSON" : "Generar JSON"}
                                </Button>
                            </Paper>
                            <Paper variant="outlined" sx={{ p: 2, position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>
                                    RESUMEN DE CONFIGURACIÓN
                                </Typography>
                                <Typography variant="body2">**Broker:** {config.broker}:{config.port}</Typography>
                                <Typography variant="body2">**Usuario:** {config.user}</Typography>
                                <Typography variant="body2">**QoS:** {getLabelFromValue(QOS_OPTIONS, config.qos)}</Typography>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" color={formDirty ? 'error' : 'inherit'}>**Formulario Modificado:** {formDirty ? 'Sí' : 'No'}</Typography>
                                    <Typography variant="body2" color={jsonNeedsUpdate ? 'warning.main' : 'inherit'}>**JSON Actualizado:** {jsonNeedsUpdate ? 'No' : 'Sí'}</Typography>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Fila inferior: Resultados del envio de la configuracion al dispositivo */}
                        <Grid item xs={12} md={12}>
                            <Paper variant="outlined" sx={{ p: 2, height: '100%', position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase'}}>
                                    Resultado envío al Dispositivo
                                </Typography>
                                <Box>
                                    <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5 }}>
                                        <strong>ConfigId: </strong>{state.configId}
                                    </Typography>
                                    <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5, color: state.color }}>
                                        <strong>Estado: </strong>{state.estado}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                        
                        {/* Botones de Acción */}
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                                <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={!formDirty}>Cancelar</Button>
                                <Button type="submit" variant="contained" disabled={jsonNeedsUpdate}>Enviar</Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Container>
    );
}

export default ConfiguracionMqttForm;